import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDialogManager } from '../../../../../../widgets/dialog';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { T } from 'src/shared/helpers';
import {
    AxisIcon,
    CollapseIcon,
    ExplodeIcon,
    FrameSelectionIcon as ResetCameraIcon,
    FullscreenExitIcon,
    FullscreenIcon,
    GlobalSpaceIcon,
    GridIcon,
    LocalSpaceIcon,
    MoveToolIcon as TranslateIcon,
    PreviewModeIcon,
    RotateToolIcon as RotateIcon,
    SaveAssetIcon as SavePositionIcon,
    ScaleToolIcon as ScaleIcon,
    SelectToolIcon as SelectionIcon,
    SwapIcon as SwitchIcon,
    TrashIcon as RemoveIcon
} from '../../../../../../widgets/icons/scene';
import SceneEditorStatusbar from './SceneEditorStatusbar';
import { MeasurementUnit } from '../../../configurator-scene-editor/useWorkspacePreferences';
import { applySelectionHighlight, SelectionHighlight } from '../../../configurator-scene-editor/selectionHighlight';
import { EditorDialog } from '../../editorDialogWindow';

const ToolbarLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

// La pastiglia bianca e il tasto del palco li usa anche il 3D del Composer (utente, 2026-10-15).
// IL VETRO DEL PROTOTIPO (utente, 2026-10-20): bianco al 95% con la sfocatura sotto, un anello appena (6%) e
// un'ombra leggera; bottoni tondi da 28 con 4px di margine e 2 fra loro.
export const ToolGroup = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px;
    background: rgb(255 255 255 / 95%);
    border-radius: 999px;
    box-shadow:
        inset 0 0 0 1px rgb(30 42 56 / 6%),
        0 1px 2px rgb(30 42 56 / 6%);
    backdrop-filter: blur(16px) saturate(1.4);
`;

const TransformToolbar = styled(ToolGroup)`
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
`;

// Il fianco destro, a metà altezza: COSA SI VEDE nel palco. Interruttori con una grammatica sola —
// ognuno dice se una cosa compare o no — e restano qui (utente, 2026-10-14). «Inquadra», che stava
// da solo nell'angolo in alto, è sceso nella barra in alto (round 4, prototipo #81): è un'azione sulla
// selezione, come gli strumenti accanto.
const ViewToolbar = styled(ToolGroup)`
    top: 50%;
    right: 15px;
    flex-direction: column;
    transform: translateY(-50%);
`;

const ExplodeToolbar = styled(ToolGroup)`
    top: 50%;
    left: 15px;
    flex-direction: column;
    transform: translateY(-50%);
`;

const ExplodeAndCollapsePanel = styled.div`
    position: absolute;
    top: 50%;
    left: 70px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    background: ${(props) => props.theme.app.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    transform: translateY(-50%);
`;

const PreviewToolbar = styled(ToolGroup)`
    right: 15px;
    bottom: 40px;
`;

const ToolSeparator = styled.span`
    width: 1px;
    height: 18px;
    margin: 0 4px;
    background: ${(props) => props.theme.app.borderColor};
`;

export const ToolButton = styled.button<{ $isActive?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    /* PREMUTO È UN LAVATO CON L'ARANCIO (l'accent; utente 2026-10-22), il passaggio un fondo neutro, a riposo il grigio muted (prototipo, icon-btn). */
    color: ${(props) => (props.$isActive ? `var(--editor-accent, ${props.theme.app.secondary})` : `var(--editor-muted, ${props.theme.app.textColor})`)};
    background: ${(props) => (props.$isActive ? 'var(--editor-accent-wash, rgb(232 101 10 / 8%))' : 'transparent')};
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$isActive ? 'var(--editor-accent-wash, rgb(232 101 10 / 8%))' : 'var(--editor-sunken, #e9ebed)')};
    }

    &:focus-visible {
        outline: 2px solid var(--editor-accent, ${(props) => props.theme.app.secondary});
        outline-offset: 1px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    &:hover:not(:disabled):not([aria-pressed='true']) {
        color: var(--editor-fg, ${(props) => props.theme.app.textColor});
    }

    svg {
        width: 15px;
        height: 15px;
    }
`;

const SpaceToggle = styled(ToolButton)`
    width: auto;
    gap: 4px;
    padding: 0 9px 0 7px;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
`;

enum EditingMode {
    None = 'None',
    Translate = 'Translate',
    Rotate = 'Rotate',
    Scaling = 'Scaling'
}

type TransformSpace = 'global' | 'local';

interface SceneEditorToolbarProps {
    sceneEditor?: any;
    stageRef?: RefObject<HTMLDivElement | null>;
    /* IL FONDALE LO TIENE CHI STA SOPRA, non questa barra: la sezione World lo accende entrando, e
       due posti che decidono la stessa cosa si contraddicono al primo di loro che non lo sa. Qui
       resta il tasto — è un modo di GUARDARE la scena, e i modi di guardare stanno sul palco. */
    previewActive?: boolean;
    onPreviewToggle?: () => void;
    measurementUnit?: MeasurementUnit;
    onMeasurementUnitChange?: (unit: MeasurementUnit) => void;
    selectionHighlight?: SelectionHighlight;
    /** V, W, E, R e F da soli scelgono gli strumenti: si spengono da Settings → Keyboard. */
    singleKeyShortcuts?: boolean;
}

const SceneEditorToolbar: FC<SceneEditorToolbarProps> = ({
    sceneEditor,
    stageRef,
    previewActive = false,
    onPreviewToggle,
    measurementUnit = 'm',
    onMeasurementUnitChange,
    selectionHighlight,
    singleKeyShortcuts = true
}) => {
    const [currentMode, setCurrentMode] = useState<EditingMode>(EditingMode.None);
    const [transformSpace, setTransformSpace] = useState<TransformSpace>('global');
    const [showGrid, setShowGrid] = useState(true);
    const [showAxes, setShowAxes] = useState(false);
    const [showSelection, setShowSelection] = useState(true);
    const [showExplodePanel, setShowExplodePanel] = useState(false);
    const [showCollapsePanel, setShowCollapsePanel] = useState(false);
    const [areExplodePositionsAvailable, setAreExplodePositionsAvailable] = useState(false);
    const [areCollapsePositionsAvailable, setAreCollapsePositionsAvailable] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const viewStateInitialized = useRef(false);
    const { showDialog, closeDialog } = useDialogManager();

    const checkPositionsAvailability = useCallback(() => {
        const zkScene = sceneEditor?.zkScene;
        setAreExplodePositionsAvailable(zkScene?.getFirstExplodedMeshLocation?.() != null);
        setAreCollapsePositionsAvailable(zkScene?.getFirstCollapsedMeshLocation?.() != null);
    }, [sceneEditor]);

    useEffect(() => {
        if (!sceneEditor) return;
        if (!viewStateInitialized.current) {
            // SI ENTRA CON LA GRIGLIA ACCESA, sempre (utente, 2026-09-15): è il riferimento con cui si
            // legge dove sta e quanto è grande ciò che si guarda. Si accende sul motore e non solo sul
            // tastino — un tastino acceso su una scena senza griglia direbbe una cosa falsa — e chi la
            // spegne la spegne per questa visita: alla prossima entrata torna.
            sceneEditor.showGround = true;
            sceneEditor.showHideGround?.(true);
            setShowGrid(true);
            // GLI ASSI PARTONO SPENTI, come nel vecchio Backoffice (utente, 2026-10-20): si accendono dal loro tasto.
            sceneEditor.showAxes = false;
            sceneEditor.showHideAxes?.(false);
            setShowAxes(false);
            viewStateInitialized.current = true;
        }
        checkPositionsAvailability();
    }, [checkPositionsAvailability, sceneEditor]);

    // L'ALONE LO DECIDE SETTINGS, NON LO STRUMENTO: vedi `useSelectionHighlight`.
    useEffect(() => {
        if (selectionHighlight) applySelectionHighlight(sceneEditor, selectionHighlight);
    }, [sceneEditor, selectionHighlight]);

    useEffect(() => {
        const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === stageRef?.current);
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [stageRef]);

    // RISPETTO A COSA SI MUOVE (QA T1, 2026-10-13): il gizmo era sempre in locale, e sui pezzi dei glb
    // nemmeno quello giusto. Si entra in Global, come nel prototipo: le frecce seguono la scena. Local le
    // fa seguire l'oggetto, anche la scala (Elena, 2026-10-15: in Global si scala lungo gli assi della
    // scena, come il collega usa). Lo chiede solo questa pagina: chi non chiama
    // `setEditControlSpace` (Composer, VTO) tiene il gizmo di prima. Si sceglie per questa visita, come
    // la griglia.
    useEffect(() => {
        sceneEditor?.setEditControlSpace?.(transformSpace);
    }, [sceneEditor, transformSpace]);

    // IL GIZMO STA SUL PIVOT (QA T2, 2026-10-13), dove il gesto avviene davvero. Al centro dell'ingombro
    // (scelta del 2026-09-14) il manico e la rotazione avevano due centri diversi, e l'oggetto girava
    // attorno a un punto che non era il gizmo.
    useEffect(() => {
        sceneEditor?.setEditControlOnPivot?.(true);
    }, [sceneEditor]);

    const selectMode = useCallback(
        (mode: EditingMode) => {
            setCurrentMode(mode);
            setShowSelection(mode === EditingMode.None);
            sceneEditor?.showHideEditControl?.(mode !== EditingMode.None);
            sceneEditor?.setEditingMode?.(mode);
        },
        [sceneEditor]
    );

    const handleSelectionClick = useCallback(() => selectMode(EditingMode.None), [selectMode]);
    const handleTranslateClick = useCallback(() => selectMode(EditingMode.Translate), [selectMode]);
    const handleRotateClick = useCallback(() => selectMode(EditingMode.Rotate), [selectMode]);
    const handleScaleClick = useCallback(() => selectMode(EditingMode.Scaling), [selectMode]);

    useEffect(() => {
        if (!singleKeyShortcuts) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            if (event.metaKey || event.ctrlKey || event.altKey || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '') || target?.isContentEditable)
                return;

            const handlers: Record<string, () => void> = {
                v: handleSelectionClick,
                w: handleTranslateClick,
                e: handleRotateClick,
                r: handleScaleClick,
                // F INQUADRA LA SCELTA, come nel prototipo e come il tasto in fondo alla barra.
                f: () => (typeof sceneEditor?.frameSelection === 'function' ? sceneEditor.frameSelection() : sceneEditor?.adjustCamera?.())
            };
            const handler = handlers[event.key.toLowerCase()];
            if (handler) {
                event.preventDefault();
                handler();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleSelectionClick, handleTranslateClick, handleRotateClick, handleScaleClick, sceneEditor, singleKeyShortcuts]);

    const handleAxisClick = () => {
        const next = !showAxes;
        if (typeof sceneEditor?.showHideAxes === 'function') {
            sceneEditor.showAxes = next;
            sceneEditor.showHideAxes(next);
        } else if (sceneEditor) {
            sceneEditor.showAxes = next;
        }
        setShowAxes(next);
    };

    const handleGridClick = () => {
        const next = !showGrid;
        if (typeof sceneEditor?.showHideGround === 'function') {
            sceneEditor.showGround = next;
            sceneEditor.showHideGround(next);
        } else if (sceneEditor) {
            sceneEditor.showGround = next;
        }
        setShowGrid(next);
    };

    // INQUADRA CIÒ CHE È SCELTO, e senza una scelta la scena intera (QA, 2026-10-12): il tasto inquadrava
    // sempre la scena, e chi aveva scelto l'oggetto grande si ritrovava davanti tutto il resto. Lo sa fare
    // il motore (`frameSelection`); uno che non lo conosce ancora inquadra la scena, come prima.
    const handleFrameClick = () => {
        if (typeof sceneEditor?.frameSelection === 'function') sceneEditor.frameSelection();
        else sceneEditor?.adjustCamera?.();
    };

    const handleFullscreen = async () => {
        if (!stageRef?.current) return;
        if (document.fullscreenElement) {
            await document.exitFullscreen?.();
        } else {
            await stageRef.current.requestFullscreen?.();
        }
    };

    const handleExplodeClick = () => {
        setShowExplodePanel((value) => !value);
        setShowCollapsePanel(false);
    };

    const handleCollapseClick = () => {
        setShowCollapsePanel((value) => !value);
        setShowExplodePanel(false);
    };

    // LA DOMANDA NOMINA LA COSA, e il bottone fa l'azione: «Save» o «Delete», non «Yes» — e senza
    // il «No» arancione di `QuestionDialog`. Si rinuncia dalla X, come in tutte le finestre della scena.
    const confirm = (id: string, request: { title: string; message: string; action: string; destructive?: boolean; onConfirm: () => void }) => {
        showDialog(
            id,
            <EditorDialog
                title={request.title}
                size='small'
                closeOnEscape
                onClose={() => closeDialog(id)}
                buttons={[
                    {
                        label: request.action,
                        destructive: request.destructive,
                        onClick: () => {
                            closeDialog(id);
                            request.onConfirm();
                        }
                    }
                ]}
            >
                <p>{request.message}</p>
            </EditorDialog>
        );
    };

    const handleSaveExplode = () =>
        confirm('save-explode-dialog', {
            title: T._('Save exploded positions', 'Admin'),
            message: T._('Save the current positions as the exploded view?', 'Admin'),
            action: T._('Save', 'Admin'),
            onConfirm: () => {
                sceneEditor?.saveExplodedMeshLocation?.();
                setAreExplodePositionsAvailable(true);
            }
        });

    const handleRemoveExplode = () =>
        confirm('delete-explode-dialog', {
            title: T._('Delete exploded positions', 'Admin'),
            message: T._('Delete the saved exploded positions?', 'Admin'),
            action: T._('Delete', 'Admin'),
            destructive: true,
            onConfirm: () => {
                const location = sceneEditor?.zkScene?.getFirstExplodedMeshLocation?.();
                if (location) sceneEditor.zkScene.get('meshLocations').remove(location);
                setAreExplodePositionsAvailable(false);
            }
        });

    const handleSaveCollapse = () =>
        confirm('save-collapse-dialog', {
            title: T._('Save collapsed positions', 'Admin'),
            message: T._('Save the current positions as the collapsed view?', 'Admin'),
            action: T._('Save', 'Admin'),
            onConfirm: () => {
                sceneEditor?.saveCollapsedMeshLocation?.();
                setAreCollapsePositionsAvailable(true);
            }
        });

    const handleRemoveCollapse = () =>
        confirm('delete-collapse-dialog', {
            title: T._('Delete collapsed positions', 'Admin'),
            message: T._('Delete the saved collapsed positions?', 'Admin'),
            action: T._('Delete', 'Admin'),
            destructive: true,
            onConfirm: () => {
                const location = sceneEditor?.zkScene?.getFirstCollapsedMeshLocation?.();
                if (location) sceneEditor.zkScene.get('meshLocations').remove(location);
                setAreCollapsePositionsAvailable(false);
            }
        });

    if (!sceneEditor) return null;

    return (
        <ToolbarLayer>
            <TransformToolbar aria-label={T._('Transform tools', 'Admin')}>
                {/* UN INTERRUTTORE SOLO CON LA SUA PAROLA (prototipo): dice in che spazio si muove, e un clic passa all'altro. */}
                <ZTooltip
                    title={transformSpace === 'global' ? T._('Switch to local space', 'Admin') : T._('Switch to global space', 'Admin')}
                    position='bottom'
                >
                    <SpaceToggle
                        type='button'
                        onClick={() => setTransformSpace(transformSpace === 'global' ? 'local' : 'global')}
                        aria-label={transformSpace === 'global' ? T._('Global axes', 'Admin') : T._('Local axes', 'Admin')}
                    >
                        {transformSpace === 'global' ? <GlobalSpaceIcon /> : <LocalSpaceIcon />}
                        <span>{transformSpace === 'global' ? T._('Global', 'Admin') : T._('Local', 'Admin')}</span>
                    </SpaceToggle>
                </ZTooltip>
                <ToolSeparator />
                <ZTooltip title={T._('Select · V', 'Admin')} position='bottom'>
                    <ToolButton $isActive={showSelection} onClick={handleSelectionClick} aria-label={T._('Select tool', 'Admin')} aria-pressed={showSelection}>
                        <SelectionIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Move · W', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Translate}
                        onClick={handleTranslateClick}
                        aria-label={T._('Move tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Translate}
                    >
                        <TranslateIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Rotate · E', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Rotate}
                        onClick={handleRotateClick}
                        aria-label={T._('Rotate tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Rotate}
                    >
                        <RotateIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Scale · R', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Scaling}
                        onClick={handleScaleClick}
                        aria-label={T._('Scale tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Scaling}
                    >
                        <ScaleIcon />
                    </ToolButton>
                </ZTooltip>
                <ToolSeparator />
                <ZTooltip title={T._('Frame selection · F', 'Admin')} position='bottom'>
                    <ToolButton onClick={handleFrameClick} aria-label={T._('Frame selection', 'Admin')}>
                        <ResetCameraIcon />
                    </ToolButton>
                </ZTooltip>
            </TransformToolbar>

            <ViewToolbar aria-label={T._('Viewport', 'Admin')}>
                {/* IN PREVIEW GRIGLIA E ASSI NON CI SONO, e i loro tasti lo dicono (utente, 2026-10-16): spenti e
                    grigi, invece di restare accesi su qualcosa che il palco non disegna. Il motore li nasconde da sé
                    nel modo Viewer; tornando in Editor ricompaiono come erano stati lasciati. */}
                <ZTooltip title={previewActive ? T._('Not shown in preview mode', 'Admin') : T._('Grid', 'Admin')} position='left'>
                    <ToolButton
                        onClick={handleGridClick}
                        disabled={previewActive}
                        $isActive={showGrid && !previewActive}
                        aria-label={T._('Show or hide grid', 'Admin')}
                        aria-pressed={showGrid && !previewActive}
                    >
                        <GridIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={previewActive ? T._('Not shown in preview mode', 'Admin') : T._('Axis', 'Admin')} position='left'>
                    <ToolButton
                        onClick={handleAxisClick}
                        disabled={previewActive}
                        $isActive={showAxes && !previewActive}
                        aria-label={T._('Show or hide axes', 'Admin')}
                        aria-pressed={showAxes && !previewActive}
                    >
                        <AxisIcon />
                    </ToolButton>
                </ZTooltip>
                {/* IL FONDALE STA IN BASSO A DESTRA, non fra questi: è l'interruttore «Preview mode», e
                    da lì commuta la scena intera invece di una cosa sola. Questi tre dicono cosa compare
                    nel palco; quello dice con quale dei due modi si disegna tutto.

                    LA ROTAZIONE AUTOMATICA NON STA PIÙ QUI. Questi tasti sono i modi di GUARDARE la
                    scena mentre ci si lavora — la griglia, il fondale, gli assi — e non si salvano con
                    il prodotto; la rotazione automatica invece è un'impostazione della camera, si
                    salva nella scena e si legge accanto a campo visivo e limiti, nel pannello che
                    apre «Free camera» della sezione Cameras. */}
            </ViewToolbar>

            <ExplodeToolbar aria-label={T._('Model state tools', 'Admin')}>
                <ZTooltip title={T._('Explode tools', 'Admin')} position='left'>
                    <ToolButton
                        $isActive={showExplodePanel}
                        onClick={handleExplodeClick}
                        aria-label={T._('Explode tools', 'Admin')}
                        aria-pressed={showExplodePanel}
                    >
                        <ExplodeIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Collapse tools', 'Admin')} position='left'>
                    <ToolButton
                        $isActive={showCollapsePanel}
                        onClick={handleCollapseClick}
                        aria-label={T._('Collapse tools', 'Admin')}
                        aria-pressed={showCollapsePanel}
                    >
                        <CollapseIcon />
                    </ToolButton>
                </ZTooltip>
            </ExplodeToolbar>

            {(showExplodePanel || showCollapsePanel) && (
                <ExplodeAndCollapsePanel>
                    {showExplodePanel && (
                        <>
                            <ZTooltip title={T._('Explode model', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areExplodePositionsAvailable}
                                    onClick={() => sceneEditor.explodeMeshes()}
                                    aria-label={T._('Explode model', 'Admin')}
                                >
                                    <SwitchIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Save exploded position', 'Admin')} position='left'>
                                <ToolButton onClick={handleSaveExplode} aria-label={T._('Save exploded position', 'Admin')}>
                                    <SavePositionIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Remove exploded position', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areExplodePositionsAvailable}
                                    onClick={handleRemoveExplode}
                                    aria-label={T._('Remove exploded position', 'Admin')}
                                >
                                    <RemoveIcon />
                                </ToolButton>
                            </ZTooltip>
                        </>
                    )}
                    {showCollapsePanel && (
                        <>
                            <ZTooltip title={T._('Collapse model', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areCollapsePositionsAvailable}
                                    onClick={() => sceneEditor.collapseMeshes()}
                                    aria-label={T._('Collapse model', 'Admin')}
                                >
                                    <SwitchIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Save collapsed position', 'Admin')} position='left'>
                                <ToolButton onClick={handleSaveCollapse} aria-label={T._('Save collapsed position', 'Admin')}>
                                    <SavePositionIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Remove collapsed position', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areCollapsePositionsAvailable}
                                    onClick={handleRemoveCollapse}
                                    aria-label={T._('Remove collapsed position', 'Admin')}
                                >
                                    <RemoveIcon />
                                </ToolButton>
                            </ZTooltip>
                        </>
                    )}
                </ExplodeAndCollapsePanel>
            )}

            <PreviewToolbar>
                {/* I DUE MODI DI DISEGNARE LA SCENA — vedi `useSceneBackground`. Premuto, la scena si
                    disegna come la vedrà chi compra: il suo environment, il pavimento, l'antialiasing; la
                    selezione, con alone e gizmo, resta. È il gesto che per qualche settimana ha abitato «Preview»
                    in fascia, e torna qui perché guardare non è provare: la prova del cliente è un'altra
                    pagina, e quel tasto adesso porta lì. */}
                <ZTooltip title={T._('Preview mode', 'Admin')} position='top'>
                    <ToolButton
                        $isActive={previewActive}
                        onClick={() => onPreviewToggle?.()}
                        aria-label={T._('Preview mode', 'Admin')}
                        aria-pressed={previewActive}
                    >
                        <PreviewModeIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')} position='top'>
                    <ToolButton
                        $isActive={isFullscreen}
                        onClick={handleFullscreen}
                        aria-pressed={isFullscreen}
                        aria-label={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')}
                    >
                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </ToolButton>
                </ZTooltip>
            </PreviewToolbar>

            {onMeasurementUnitChange && (
                <SceneEditorStatusbar sceneEditor={sceneEditor} measurementUnit={measurementUnit} onMeasurementUnitChange={onMeasurementUnitChange} />
            )}
        </ToolbarLayer>
    );
};

export default SceneEditorToolbar;
